
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
    ReactFlow,
    MiniMap,
    Background,
    BackgroundVariant,
    useNodesState,
    useEdgesState,
    useReactFlow,
    useUpdateNodeInternals,
    addEdge,
    Connection,
    Panel,
    type Node,
    type Edge,
    MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { nodeTypes } from './nodes';
import { edgeTypes } from './edges';
import { WorkflowToolbox } from './WorkflowToolbox';
import { WorkflowNavigationProvider, useWorkflowNavigation } from './WorkflowNavigationContext';
import { NodeSettingsProvider, useNodeSettings } from './NodeSettingsContext';
import { useWorkflowModeOptional, WorkflowModeProvider } from './WorkflowModeContext';
import NodeSettingsDrawer from './NodeSettingsDrawer';
import EditWorkflowDrawer from './EditWorkflowDrawer';
import EditToolDrawer from './EditToolDrawer';
import ValidationIssuesDrawer from './ValidationIssuesDrawer';
import ExportAslDrawer from './ExportAslDrawer';
import ImportAslDrawer from './ImportAslDrawer';
import DynamicNextDrawer from './DynamicNextDrawer';
import { TaskConfigDrawer, type CapabilityMetadata } from './task-config';
import { WorkflowBreadcrumb } from './WorkflowBreadcrumb';
import { WorkflowDebugToolbar } from './WorkflowDebugToolbar';
import { WorkflowReplayToolbar } from './WorkflowReplayToolbar';
import { NodeInspectorDrawer } from './NodeInspectorDrawer';
import { CreateCapabilityDrawer } from '@/components/capabilities/CreateCapabilityDrawer';
import ToolFormDrawer from '@/components/tools/ToolFormDrawer';
import { useWorkflowDebug } from '@/hooks/useWorkflowDebug';
import { useWorkflowReplay } from '@/hooks/useWorkflowReplay';
import type { CheckpointData, LogEntry, AggregatedCheckpointData } from '@/types/workflow-replay';
import { convertAslToReactFlow, type ResourceRegistry } from './utils/asl-converter';
import { getWorkflowCapabilityFqcn } from './utils/capability-fqcn';
import { validateConnection, wouldCreateCycle, validateGraph } from './utils/connection-validator';
import { exportToAsl, isExportReady } from './utils/asl-exporter';
import { FlowProvider, DeleteConfirmationDialog, useFlowKeyboard, useDeleteConfirmation, getLayoutedElements } from '@/components/react-flow';
import { TiledWindowContainer, TiledWindowProvider, useTiledWindowsOptional } from '@/components/tiled-windows';
import type { 
    AslDefinition, 
    WorkflowNode, 
    WorkflowEdge as WorkflowEdgeType,
    WorkflowNodeData,
    StateNodeData,
    WorkflowStackItem,
    WorkflowEdgeData,
    EdgeType,
    ValidationIssue,
    OpenTiledWindowRequest,
    CapabilityType,
} from '@/types/workflow-graph';

// ==========================================================================
// Layout Calculation (Pure Function - No React State)
// ==========================================================================

// Node dimensions must match the actual rendered WorkflowCard component
// WorkflowCard is w-72 = 288px, min-h-32 = 128px
// Must stay in sync with asl-converter.ts and layout.ts
const LAYOUT_NODE_WIDTH = 288;
const LAYOUT_NODE_HEIGHT = 128;

interface LayoutOptions {
    direction: 'LR' | 'TB';
    nodeSeparation?: number;
    rankSeparation?: number;
}

/**
 * Calculate full layout for workflow graph including container children.
 * This is a pure function that can be called before rendering to avoid visual flash.
 * 
 * Container nodes (mapContainer) have their children laid out in the OPPOSITE
 * direction to the main layout for visual clarity.
 */
function calculateFullLayout(
    inputNodes: Node<WorkflowNodeData>[],
    edges: Edge<WorkflowEdgeData>[],
    options: LayoutOptions
): Node<WorkflowNodeData>[] {
    const { direction, nodeSeparation = 50, rankSeparation = 150 } = options;
    const childDirection = direction === 'LR' ? 'TB' : 'LR';
    
    // Container padding configuration
    const CONTAINER_PADDING_START = 120;
    const CONTAINER_PADDING_END = 100;
    const CONTAINER_PADDING_CROSS_START = 80;
    const CONTAINER_PADDING_CROSS_END = 60;

    // Work with a mutable copy
    let workingNodes = [...inputNodes];

    // =======================================================================
    // Step 1: Layout children inside each container FIRST
    // =======================================================================
    
    const containerNodes = workingNodes.filter((n) => n.type === 'mapContainer');
    const repositionedChildrenMap = new Map<string, Node<WorkflowNodeData>[]>();

    for (const container of containerNodes) {
        const childNodes = workingNodes.filter((n) => n.parentId === container.id);
        if (childNodes.length === 0) continue;

        const childNodeIds = new Set(childNodes.map((n) => n.id));
        const childEdges = edges.filter(
            (e) => childNodeIds.has(e.source) && childNodeIds.has(e.target)
        );

        // Temporarily remove parentId for layout calculation
        const childNodesForLayout = childNodes.map((n) => ({
            ...n,
            parentId: undefined,
            position: { ...n.position },
        }));

        // Layout children in OPPOSITE direction
        const { nodes: layoutedChildren } = getLayoutedElements(
            childNodesForLayout as Node<WorkflowNodeData>[],
            childEdges,
            {
                direction: childDirection,
                nodeSeparation: 40,
                rankSeparation: 120,
            }
        );

        // Find bounds of layouted children (use explicit dimensions set during ASL conversion)
        let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;
        layoutedChildren.forEach((child) => {
            const width = child.width ?? LAYOUT_NODE_WIDTH;
            const height = child.height ?? LAYOUT_NODE_HEIGHT;
            minX = Math.min(minX, child.position.x);
            minY = Math.min(minY, child.position.y);
            maxX = Math.max(maxX, child.position.x + width);
            maxY = Math.max(maxY, child.position.y + height);
        });

        if (!isFinite(minX)) {
            minX = 0; minY = 0; maxX = LAYOUT_NODE_WIDTH; maxY = LAYOUT_NODE_HEIGHT;
        }

        // Offset children to fit within container padding
        const offsetX = childDirection === 'LR' 
            ? CONTAINER_PADDING_START - minX 
            : CONTAINER_PADDING_CROSS_START - minX;
        const offsetY = childDirection === 'LR' 
            ? CONTAINER_PADDING_CROSS_START - minY
            : CONTAINER_PADDING_START - minY;

        const repositionedChildren = layoutedChildren.map((child) => ({
            ...child,
            parentId: container.id,
            extent: 'parent' as const,
            expandParent: true,
            zIndex: 1,
            position: {
                x: child.position.x + offsetX,
                y: child.position.y + offsetY,
            },
        }));

        repositionedChildrenMap.set(container.id, repositionedChildren as Node<WorkflowNodeData>[]);

        // Calculate new container size
        const childrenWidth = maxX - minX;
        const childrenHeight = maxY - minY;

        let newContainerWidth: number;
        let newContainerHeight: number;

        if (childDirection === 'LR') {
            newContainerWidth = Math.max(500, childrenWidth + CONTAINER_PADDING_START + CONTAINER_PADDING_END);
            newContainerHeight = Math.max(280, childrenHeight + CONTAINER_PADDING_CROSS_START + CONTAINER_PADDING_CROSS_END);
        } else {
            newContainerWidth = Math.max(400, childrenWidth + CONTAINER_PADDING_CROSS_START + CONTAINER_PADDING_CROSS_END);
            newContainerHeight = Math.max(350, childrenHeight + CONTAINER_PADDING_START + CONTAINER_PADDING_END);
        }

        // Update container dimensions
        workingNodes = workingNodes.map((n) => {
            if (n.id === container.id) {
                return {
                    ...n,
                    width: newContainerWidth,
                    height: newContainerHeight,
                    style: { ...n.style, width: newContainerWidth, height: newContainerHeight },
                    data: {
                        ...n.data,
                        containerWidth: newContainerWidth,
                        containerHeight: newContainerHeight,
                    },
                };
            }
            return n;
        });
    }

    // =======================================================================
    // Step 2: Layout root nodes WITH the correct container sizes
    // =======================================================================
    
    const { nodes: rootLayoutedNodes } = getLayoutedElements(
        workingNodes,
        edges,
        { direction, nodeSeparation, rankSeparation }
    );

    // =======================================================================
    // Step 3: Merge repositioned children and set layoutDirection
    // =======================================================================
    
    return rootLayoutedNodes.map((n) => {
        // Check if this node is a repositioned child
        for (const [, children] of repositionedChildrenMap) {
            const repositioned = children.find((c) => c.id === n.id);
            if (repositioned) {
                return {
                    ...repositioned,
                    data: { ...repositioned.data, layoutDirection: childDirection },
                };
            }
        }
        // Root nodes use the main layout direction
        return {
            ...n,
            data: { ...n.data, layoutDirection: direction },
        };
    });
}
import { capabilityColors, defaultTaskColors } from '@/lib/capability-colors';
import { AlertTriangle, Settings, ArrowRightFromLine, ArrowDownFromLine, Download, Upload, Bug, History, Plus, Minus, Maximize2, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/** Workflow metadata from workflow.json */
interface WorkflowMetadata {
    name: string;
    version?: string;
    description?: string;
    type?: string;
    adapter?: string;
    capabilities?: Record<string, {
        description?: string;
        input?: {
            type?: string;
            properties?: Record<string, unknown>;
            required?: string[];
        };
        output?: {
            type?: string;
            properties?: Record<string, unknown>;
        };
    }>;
}

interface WorkflowGraphProps {
    definition: AslDefinition;
    workflowId: string;
    workflowKey: string;
    workflowName: string;
    /** App ID when workflow is scoped to an app */
    appSlug?: string;
    readOnly?: boolean;
    onDefinitionChange?: (definition: AslDefinition) => void;
    /** Callback when graph changes (dirty state). Fires with isDirty=true when modified, isDirty=false when reverted.
     * Also provides the current definition if it can be exported (valid graph), or null if there are validation errors.
     */
    onGraphChange?: (isDirty: boolean, definition: AslDefinition | null) => void;
    /** Callback when node settings drawer opens */
    onNodeSettingsOpen?: (nodeData: StateNodeData) => void;
    /** Callback when node settings drawer closes */
    onNodeSettingsClose?: () => void;
    /** Available workflows for sub-workflow navigation */
    availableWorkflows?: Array<{ id: string; key: string; name: string; fqcn?: string; app_key?: string; definition: AslDefinition }>;
    /** Available agents for toolbox (include fqcn for ASL Resource id) */
    agents?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    /** Available tools for toolbox */
    tools?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    /** Available models for toolbox */
    models?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    /** Available policies for toolbox */
    policies?: Array<{ name: string; fqcn?: string; description?: string; app_key?: string }>;
    /** Workflow metadata (from workflow.json) */
    workflowMetadata?: WorkflowMetadata;
    /** Whether this workflow has associated test.json file (enables debug mode) */
    hasTests?: boolean;
    /** Whether this workflow has workflow runs in the database (enables replay mode) */
    hasRuns?: boolean;
    /** Callback to open an entity in a tiled window (drill-down navigation) */
    onOpenTiledWindow?: (request: OpenTiledWindowRequest) => void;
    /** Whether this graph is embedded inside a tiled window (skips nested TiledWindowContainer) */
    embedded?: boolean;
    /** Initial replay run ID - automatically enters replay mode and loads this run */
    initialReplayRunId?: string;
    /** Tailwind classes for the main graph panel background (default solid white) */
    graphSurfaceClassName?: string;
    /** Let the parent provide the canvas fill (transparent React Flow + dot pattern layer); use with e.g. bg-white on the page */
    transparentCanvas?: boolean;
}

function WorkflowGraphInner({
    readOnly = true,
    availableWorkflows = [],
    onDefinitionChange,
    onGraphChange,
    workflowMetadata,
    hasTests = false,
    hasRuns = false,
    onOpenTiledWindow,
    embedded = false,
    appSlug,
    graphSurfaceClassName,
    transparentCanvas = false,
}: {
    readOnly?: boolean;
    availableWorkflows?: Array<{ id: string; key: string; name: string; fqcn?: string; app_key?: string; definition: AslDefinition }>;
    onDefinitionChange?: (definition: AslDefinition) => void;
    onGraphChange?: (isDirty: boolean, definition: AslDefinition | null) => void;
    workflowMetadata?: WorkflowMetadata;
    hasTests?: boolean;
    hasRuns?: boolean;
    onOpenTiledWindow?: (request: OpenTiledWindowRequest) => void;
    embedded?: boolean;
    appSlug?: string;
    graphSurfaceClassName?: string;
    transparentCanvas?: boolean;
}) {
    const graphSurface = transparentCanvas ? 'bg-transparent rf-transparent-canvas' : (graphSurfaceClassName ?? 'bg-white');

    const graphContainerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!transparentCanvas || !graphContainerRef.current) return;
        const container = graphContainerRef.current;
        const clearBg = () => {
            container.querySelectorAll<HTMLElement>('.react-flow, .react-flow__background').forEach((el) => {
                el.style.setProperty('background-color', 'transparent', 'important');
                el.style.setProperty('background', 'transparent', 'important');
            });
        };
        clearBg();
        const observer = new MutationObserver(clearBg);
        observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
        return () => observer.disconnect();
    }, [transparentCanvas]);
    const { currentWorkflow, pushWorkflow } = useWorkflowNavigation();
    const { selectedNode, isSettingsOpen, closeSettings, readOnly: settingsReadOnly, availableResources } = useNodeSettings();

    // Probe DB for runs so the Replay button enables even when the page omits hasRuns.
    const [detectedHasRuns, setDetectedHasRuns] = useState(hasRuns);
    useEffect(() => {
        if (hasRuns) {
            setDetectedHasRuns(true);
            return;
        }
        const identifier = currentWorkflow?.key || currentWorkflow?.id;
        if (!identifier) {
            setDetectedHasRuns(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await fetch(
                    `/api/workflows/${encodeURIComponent(identifier)}/runs?per_page=1`,
                    {
                        method: 'GET',
                        headers: { Accept: 'application/json' },
                        credentials: 'same-origin',
                    },
                );
                if (!response.ok || cancelled) return;
                const body = await response.json();
                const total = body?.meta?.total ?? body?.data?.length ?? 0;
                if (!cancelled) {
                    setDetectedHasRuns(Number(total) > 0);
                }
            } catch {
                if (!cancelled) setDetectedHasRuns(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [hasRuns, currentWorkflow?.key, currentWorkflow?.id]);
    const effectiveHasRuns = hasRuns || detectedHasRuns;

    const resolvedResources = useMemo(() => {
        const withFqcn = <T extends { name: string; fqcn?: string; app_key?: string }>(items: T[]) =>
            items.map((item) => ({
                ...item,
                fqcn: getWorkflowCapabilityFqcn(
                    { fqcn: item.fqcn, app_key: item.app_key, name: item.name },
                    appSlug,
                ),
            }));
        return {
            ...availableResources,
            agents: withFqcn(availableResources.agents),
            tools: withFqcn(availableResources.tools),
            models: withFqcn(availableResources.models),
            policies: withFqcn(availableResources.policies),
        };
    }, [availableResources, appSlug]);
    const { screenToFlowPosition } = useReactFlow();
    const updateNodeInternals = useUpdateNodeInternals();
    
    // Only access tiled windows context when not embedded (to avoid infinite recursion)
    const tiledWindowsContext = useTiledWindowsOptional();
    const tiledWindows = embedded || !tiledWindowsContext ? [] : tiledWindowsContext.windows;

    // Default handler: if the parent didn't provide onOpenTiledWindow, use the
    // built-in TiledWindowProvider that WorkflowGraph wraps itself in.
    const defaultOpenTiledWindow = useCallback((request: OpenTiledWindowRequest) => {
        if (!tiledWindowsContext) return;
        tiledWindowsContext.openWindow({
            entityType: request.entityType,
            entityId: request.entityId,
            entityName: request.entityName,
            appKey: request.appKey || appSlug,
            parentWindowId: request.parentWindowId,
            replayRunId: request.replayRunId,
        });
    }, [tiledWindowsContext, appSlug]);

    const effectiveOnOpenTiledWindow = onOpenTiledWindow ?? (tiledWindowsContext ? defaultOpenTiledWindow : undefined);

    const [isToolboxOpen, setIsToolboxOpen] = useState(false);
    const [isToolboxPinned, setIsToolboxPinned] = useState(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Check if any tiled windows are open (disables sidebar hover when windows cover the graph)
    const hasTiledWindowsOpen = tiledWindows.length > 0;
    
    // Track previous window count to detect when windows close
    const prevWindowCountRef = useRef(tiledWindows.length);
    // Suppress hover trigger after window closes (requires mouse to leave and re-enter)
    const [isHoverSuppressed, setIsHoverSuppressed] = useState(false);
    
    // Detect when a tiled window closes and suppress hover trigger
    useEffect(() => {
        if (tiledWindows.length < prevWindowCountRef.current) {
            // Window was closed - suppress hover trigger
            setIsHoverSuppressed(true);
        }
        prevWindowCountRef.current = tiledWindows.length;
    }, [tiledWindows.length]);

    // Handle delayed close for hover behavior
    const handleToolboxMouseEnter = useCallback(() => {
        // Don't open if hover is suppressed (window just closed)
        if (isHoverSuppressed) return;
        
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        setIsToolboxOpen(true);
    }, [isHoverSuppressed]);

    const handleToolboxMouseLeave = useCallback(() => {
        // Clear suppression when mouse leaves the hover zone
        setIsHoverSuppressed(false);
        
        if (isToolboxPinned) return;
        
        // Delay closing to allow moving between hover zone and toolbox
        closeTimeoutRef.current = setTimeout(() => {
            setIsToolboxOpen(false);
        }, 300);
    }, [isToolboxPinned]);
    const [validationStatus, setValidationStatus] = useState<{ canExport: boolean; issues: ValidationIssue[] }>({ canExport: true, issues: [] });
    const [isWorkflowSettingsOpen, setIsWorkflowSettingsOpen] = useState(false);
    const [layoutDirection, setLayoutDirection] = useState<'LR' | 'TB'>('LR');

    // Export drawer state
    const [exportDrawer, setExportDrawer] = useState<{
        isOpen: boolean;
        aslJson: string;
    }>({
        isOpen: false,
        aslJson: '',
    });

    // Import drawer state
    const [isImportDrawerOpen, setIsImportDrawerOpen] = useState(false);

    // Agent/Tool creation drawer state (for drag-drop new agent/tool tasks)
    const [isAgentFormDrawerOpen, setIsAgentFormDrawerOpen] = useState(false);
    const [isToolFormDrawerOpen, setIsToolFormDrawerOpen] = useState(false);
    
    // Store drop position for creating node after agent/tool creation
    // Using ref to avoid stale closure issues when callback is captured by the drawer
    const pendingNodeDropRef = useRef<{
        position: { x: number; y: number };
        parentId?: string;
    } | null>(null);

    // Validation issues drawer state
    const [validationDrawer, setValidationDrawer] = useState<{
        isOpen: boolean;
        errors: ValidationIssue[];
        warnings: ValidationIssue[];
    }>({
        isOpen: false,
        errors: [],
        warnings: [],
    });

    // Workflow debug hook - provides debug mode, breakpoints, and controls
    // capabilityName: Full name for test discovery (e.g., "workflow.casefile.ingest")
    // workflowKey: Database key for workflow execution
    const debug = useWorkflowDebug({
        capabilityName: workflowMetadata?.name || currentWorkflow?.name || '',
        workflowKey: currentWorkflow?.key || '',
        onNodeEntered: (nodeId) => {
            console.log('[Debug] Node entered:', nodeId);
        },
        onPaused: (nodeId) => {
            console.log('[Debug] Paused at:', nodeId);
        },
        onCompleted: () => {
            toast.success('Debug session completed');
        },
    });

    // Get replay context from navigation (for sub-workflow replay navigation)
    const { currentReplayRunId } = useWorkflowNavigation();
    
    // Get global mode context - this is the single source of truth for replay mode
    const globalMode = useWorkflowModeOptional();
    
    // Replay mode is determined by the global context
    const isReplayMode = globalMode?.mode === 'replay';
    
    // Determine the effective initial run ID for child workflows:
    // 1. Use currentReplayRunId from navigation context (explicit subflow navigation)
    // 2. Or look up child run from global mode's child run cache
    const effectiveInitialRunId = useMemo(() => {
        // Navigation context takes precedence (explicit subflow navigation)
        if (currentReplayRunId) {
            return currentReplayRunId;
        }
        
        // If global mode is replay, try to find child run for this workflow
        if (isReplayMode && globalMode?.rootReplayRunId && currentWorkflow?.key) {
            const childRunId = globalMode.getChildRunId(globalMode.rootReplayRunId, currentWorkflow.key);
            if (childRunId) {
                console.log('[WorkflowGraph] Using child run from global mode:', {
                    parentRunId: globalMode.rootReplayRunId,
                    workflowKey: currentWorkflow.key,
                    childRunId,
                });
                return childRunId;
            }
        }
        
        return undefined;
    }, [currentReplayRunId, isReplayMode, globalMode, currentWorkflow?.key]);

    // Workflow replay hook - manages replay data (runs, checkpoints, logs)
    // isReplayMode is controlled by the global mode context
    const replay = useWorkflowReplay({
        workflowId: currentWorkflow?.id || '',
        workflowKey: currentWorkflow?.key || '',
        isReplayMode,
        initialRunId: effectiveInitialRunId,
        onNodeSelected: (stateName, checkpoint) => {
            console.log('[Replay] Node selected:', stateName, checkpoint);
        },
    });
    
    // Register child runs in the global mode context when they're discovered
    useEffect(() => {
        if (globalMode && isReplayMode && replay.selectedRunId && replay.childRunsByState.size > 0) {
            for (const [stateName, childRun] of replay.childRunsByState) {
                if (childRun.workflow_key) {
                    globalMode.registerChildRun({
                        parentRunId: replay.selectedRunId,
                        childWorkflowKey: childRun.workflow_key,
                        childRunId: childRun.id,
                        stateName,
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalMode, isReplayMode, replay.selectedRunId]);
    
    // Enter replay mode - updates global mode
    const handleEnterReplayMode = useCallback(() => {
        if (globalMode) {
            globalMode.enterReplayMode(null); // Will be updated when a run is selected
        }
    }, [globalMode]);
    
    // Exit replay mode - updates global mode (affects all workflows)
    const handleExitReplayMode = useCallback(() => {
        if (globalMode) {
            globalMode.exitReplayMode();
        }
    }, [globalMode]);
    
    // Update global mode with the selected run ID when a run is selected
    useEffect(() => {
        if (globalMode && isReplayMode && replay.selectedRunId) {
            // Update root replay run ID if this is the first run selection or root workflow
            if (!globalMode.rootReplayRunId || !currentReplayRunId) {
                console.log('[WorkflowGraph] Updating global mode with replay run:', replay.selectedRunId);
                globalMode.enterReplayMode(replay.selectedRunId);
            }
        }
    }, [globalMode, isReplayMode, replay.selectedRunId, currentReplayRunId]);

    // Node inspector drawer state for replay mode
    const [inspectorState, setInspectorState] = useState<{
        isOpen: boolean;
        stateName: string;
        capabilityName: string;
        checkpoint: CheckpointData | null;
        logs: LogEntry[];
        aggregated: AggregatedCheckpointData | null;
    }>({
        isOpen: false,
        stateName: '',
        capabilityName: '',
        checkpoint: null,
        logs: [],
        aggregated: null,
    });

    // Dynamic routing drawer state (for Task nodes with multiple outgoing edges)
    const [dynamicNextDrawer, setDynamicNextDrawer] = useState<{
        isOpen: boolean;
        nodeId: string;
        nodeName: string;
        currentNextPath: string;
        targets: string[];
    }>({
        isOpen: false,
        nodeId: '',
        nodeName: '',
        currentNextPath: '',
        targets: [],
    });

    // Lock graph when in debug or replay mode to prevent accidental modifications
    const isGraphLocked = debug.isDebugMode || replay.isReplayMode;

    // Convert ASL to ReactFlow format and apply full layout BEFORE rendering
    // This prevents visual "flash" where nodes render then move to new positions
    const { initialNodes, initialEdges, flowKey } = useMemo(() => {
        if (!currentWorkflow?.definition) {
            return { initialNodes: [], initialEdges: [], flowKey: 'empty' };
        }
        
        // Create registry from available resources for type lookup.
        // fallbackAppKey handles legacy capabilities imported without an app_key.
        const registry: ResourceRegistry = {
            agents: resolvedResources.agents.map((a) => ({
                name: a.fqcn,
                ...(a.name !== a.fqcn ? { alias: a.name } : {}),
                app_key: a.app_key,
            })),
            tools: resolvedResources.tools.map((t) => ({
                name: t.fqcn,
                ...(t.name !== t.fqcn ? { alias: t.name } : {}),
                app_key: t.app_key,
            })),
            models: resolvedResources.models.map((m) => ({
                name: m.fqcn,
                ...(m.name !== m.fqcn ? { alias: m.name } : {}),
                app_key: m.app_key,
            })),
            workflows: availableResources.workflows.map((w) => {
                const wf = w as { fqcn?: string; app_key?: string; name: string; key?: string };
                const fqcn = wf.fqcn ?? getWorkflowCapabilityFqcn(
                    { name: wf.name, app_key: wf.app_key },
                    wf.app_key ?? appSlug,
                );
                return {
                    name: fqcn,
                    ...(wf.name !== fqcn ? { alias: wf.name } : {}),
                    app_key: wf.app_key ?? appSlug,
                };
            }),
            fallbackAppKey: appSlug,
        };
        
        const result = convertAslToReactFlow(
            currentWorkflow.definition,
            undefined, // parentId
            0,         // offsetX
            0,         // offsetY
            registry   // resource registry for type lookup
        );
        
        // Apply comprehensive layout including container children BEFORE first render
        // This uses the same logic as handleAutoLayout to ensure consistency
        const layoutedNodes = calculateFullLayout(
            result.nodes as Node<WorkflowNodeData>[],
            result.edges as Edge<WorkflowEdgeData>[],
            { direction: 'LR', nodeSeparation: 50, rankSeparation: 150 }
        );

        return { 
            initialNodes: layoutedNodes, 
            initialEdges: result.edges,
            flowKey: `${currentWorkflow.id}-${currentWorkflow.key}`
        };
    }, [currentWorkflow?.definition, currentWorkflow?.id, currentWorkflow?.key, resolvedResources, appSlug]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    // Callback to create a Task node after agent/tool creation
    const handleAgentCreated = useCallback((agent: { id?: string; name: string; description?: string | null; fqcn?: string; app_key?: string }) => {
        const pendingDrop = pendingNodeDropRef.current;
        if (!pendingDrop) return;
        
        const { position, parentId } = pendingDrop;
        const fqcn = getWorkflowCapabilityFqcn(
            { fqcn: agent.fqcn, app_key: agent.app_key, name: agent.name },
            appSlug,
        );
        
        const nodeData: StateNodeData = {
            nodeType: 'state',
            label: agent.name,
            stateType: 'Task',
            state: {
                Type: 'Task',
                Resource: `agent:${fqcn}`,
            },
            resourceType: 'agent',
            isStart: false,
            isEnd: false,
        };

        const newNode: WorkflowNode = {
            id: `task-${Date.now()}`,
            type: 'task',
            position,
            data: nodeData,
            ...(parentId && { 
                parentId, 
                extent: 'parent' as const,
                expandParent: true,
                zIndex: 1,
            }),
        };

        if (parentId) {
            setNodes((nds) => {
                const parentIndex = nds.findIndex((n) => n.id === parentId);
                const newNodes = [...nds];
                newNodes.splice(parentIndex + 1, 0, newNode);
                return newNodes;
            });
            toast.success('Added Agent Task to Map iterator', {
                description: `Agent: ${agent.name}`,
            });
        } else {
            setNodes((nds) => [...nds, newNode]);
            toast.success('Added Agent Task', {
                description: `Agent: ${agent.name}`,
            });
        }
        
        pendingNodeDropRef.current = null;
    }, [setNodes, appSlug]);

    const handleToolCreated = useCallback((tool: { id: string; name: string; description?: string; fqcn?: string; app_key?: string }) => {
        const pendingDrop = pendingNodeDropRef.current;
        if (!pendingDrop) return;
        
        const { position, parentId } = pendingDrop;
        const fqcn = getWorkflowCapabilityFqcn(
            { fqcn: tool.fqcn, app_key: tool.app_key, name: tool.name },
            appSlug,
        );
        
        const nodeData: StateNodeData = {
            nodeType: 'state',
            label: tool.name,
            stateType: 'Task',
            state: {
                Type: 'Task',
                Resource: `tool:${fqcn}`,
            },
            resourceType: 'tool',
            isStart: false,
            isEnd: false,
        };

        const newNode: WorkflowNode = {
            id: `task-${Date.now()}`,
            type: 'task',
            position,
            data: nodeData,
            ...(parentId && { 
                parentId, 
                extent: 'parent' as const,
                expandParent: true,
                zIndex: 1,
            }),
        };

        if (parentId) {
            setNodes((nds) => {
                const parentIndex = nds.findIndex((n) => n.id === parentId);
                const newNodes = [...nds];
                newNodes.splice(parentIndex + 1, 0, newNode);
                return newNodes;
            });
            toast.success('Added Tool Task to Map iterator', {
                description: `Tool: ${tool.name}`,
            });
        } else {
            setNodes((nds) => [...nds, newNode]);
            toast.success('Added Tool Task', {
                description: `Tool: ${tool.name}`,
            });
        }
        
        pendingNodeDropRef.current = null;
    }, [setNodes, appSlug]);

    // Handle node click in replay mode
    // mapNodeId is passed when clicking a node inside a map iterator
    // parallelInfo is passed when clicking a node inside a parallel branch
    const handleNodeClickInReplay = useCallback((
        nodeId: string, 
        mapNodeId?: string,
        parallelInfo?: { parallelNodeId: string; branchIndex: number }
    ) => {
        if (!replay.isReplayMode) return;

        // Extract state name from node ID (remove "state-" prefix if present)
        // Also handle iterator node IDs like "MapNode-iter-StateName"
        let stateName = nodeId.replace(/^state-/, '');
        
        // If this looks like an iterator node ID, extract the inner state name
        const iterMatch = stateName.match(/-iter-(.+)$/);
        if (iterMatch) {
            stateName = iterMatch[1];
            // If mapNodeId wasn't provided, extract it from the node ID
            if (!mapNodeId) {
                mapNodeId = stateName.replace(/-iter-.+$/, '');
            }
        }
        
        const checkpointResult = replay.getCheckpointForNode(stateName, mapNodeId, parallelInfo);
        const logs = replay.getLogsForNode(stateName);

        // Extract capability name from node data
        const node = nodes.find(n => {
            const data = n.data as WorkflowNodeData;
            return data.label === stateName || n.id === nodeId;
        });
        let capabilityName = '';
        if (node) {
            const data = node.data as WorkflowNodeData;
            if (data.nodeType === 'state') {
                const stateData = data as StateNodeData;
                // Resource format: "tool:name", "agent:name", etc.
                if (stateData.state?.Resource) {
                    capabilityName = stateData.state.Resource.replace(/^(agent|tool|model|workflow):/, '');
                }
            }
        }

        console.log('[Replay] Node clicked:', { 
            nodeId, 
            stateName, 
            capabilityName,
            mapNodeId,
            parallelInfo,
            isMapIteration: checkpointResult.isMapIteration,
            isParallelBranch: checkpointResult.isParallelBranch,
            hasAggregated: !!checkpointResult.aggregated,
            checkpoint: checkpointResult.checkpoint?.status,
        });

        setInspectorState({
            isOpen: true,
            stateName,
            capabilityName,
            checkpoint: checkpointResult.checkpoint,
            logs,
            aggregated: checkpointResult.aggregated,
        });
    }, [replay.isReplayMode, replay.getCheckpointForNode, replay.getLogsForNode, nodes]);

    // Track dirty state - compare current graph to initial definition
    // Use a ref to track the last reported dirty state to avoid redundant callbacks
    const lastDirtyStateRef = useRef<boolean>(false);
    // Baseline definition for dirty comparison - updated by the sync effect below
    // using the round-trip export of initial nodes (not the raw prop definition)
    const initialDefinitionRef = useRef<AslDefinition | null>(null);

    // Track pending auto-layout (set to flowKey when layout is needed)
    const [pendingAutoLayout, setPendingAutoLayout] = useState<string | null>(null);

    // Track the definition reference to detect when a sync is needed.
    // currentWorkflow.definition only changes on:
    //   1) Navigation to a different workflow (id/key change)
    //   2) NavigationProvider sync after save+refetch
    // It does NOT change on user edits (drag-drop, connect) or unrelated re-renders,
    // so it won't accidentally wipe user changes.
    const lastSyncedDefinitionRef = useRef<AslDefinition | null | undefined>(null);

    // Sync nodes and edges when the source definition changes
    useEffect(() => {
        if (currentWorkflow?.definition !== lastSyncedDefinitionRef.current) {
            lastSyncedDefinitionRef.current = currentWorkflow?.definition;
            setNodes(initialNodes);
            setEdges(initialEdges);
            // Trigger auto-layout after sync (state update ensures we wait for next render)
            setPendingAutoLayout(flowKey);

            // Update dirty detection baseline with the round-trip exported definition.
            // This ensures any round-trip artifacts are present in both the baseline
            // and the current export, preventing false-positive dirty state.
            const baselineResult = exportToAsl(initialNodes as WorkflowNode[], initialEdges as WorkflowEdgeType[]);
            initialDefinitionRef.current = baselineResult.definition ?? currentWorkflow?.definition ?? null;
            lastDirtyStateRef.current = false;
        }
    }, [currentWorkflow?.definition, initialNodes, initialEdges, flowKey, setNodes, setEdges]);

    // Validate graph on changes
    useEffect(() => {
        const result = validateGraph(nodes as Node<WorkflowNodeData>[], edges);
        setValidationStatus({
            canExport: result.canExport,
            issues: result.issues,
        });
    }, [nodes, edges]);
    
    // Detect changes and call onGraphChange callback
    useEffect(() => {
        if (!onGraphChange) return;
        
        // Try to export current graph to compare with initial definition
        const result = exportToAsl(nodes as WorkflowNode[], edges as WorkflowEdgeType[]);
        
        // Determine if graph is dirty by comparing definitions
        // Simple comparison: serialize both and compare strings
        const currentDefStr = result.definition 
            ? JSON.stringify(result.definition) 
            : null;
        const initialDefStr = initialDefinitionRef.current 
            ? JSON.stringify(initialDefinitionRef.current) 
            : null;
        
        const isDirty = currentDefStr !== initialDefStr;
        
        // Always fire when dirty so the parent has the latest definition for saving.
        // When clean, only fire on the transition back to clean.
        // Pass the definition even when validation fails so the parent can save work-in-progress.
        if (isDirty || isDirty !== lastDirtyStateRef.current) {
            lastDirtyStateRef.current = isDirty;
            onGraphChange(isDirty, result.definition ?? null);
        }
    }, [nodes, edges, onGraphChange]);

    // ==========================================================================
    // Auto-layout on Initial Load
    // ==========================================================================

    // We need a stable reference to handleAutoLayout for manual layout buttons
    const handleAutoLayoutRef = useRef<((showToast?: boolean) => void) | null>(null);

    // ==========================================================================
    // Delete Confirmation
    // ==========================================================================

    const handleDelete = useCallback(
        async (nodesToDelete: Node[], edgesToDelete: Edge[]) => {
            const stateNodesInGraph = nodes.filter((n) => (n.data as WorkflowNodeData).nodeType === 'state');
            const isOnlyStateInWorkflow = stateNodesInGraph.length === 1;

            const safeNodesToDelete = nodesToDelete.filter((node) => {
                if (node.id === '__start__' || (node.data as WorkflowNodeData).nodeType === 'startMarker') return false;
                const data = node.data as WorkflowNodeData;
                const isStartState = data.nodeType === 'state' && (data as StateNodeData).isStart;
                if (isStartState && !isOnlyStateInWorkflow) return false;
                return true;
            });

            // Remove nodes
            if (safeNodesToDelete.length > 0) {
                const nodeIds = new Set(safeNodesToDelete.map((n) => n.id));
                setNodes((prev) => {
                    const remaining = prev.filter((n) => !nodeIds.has(n.id));
                    
                    const remainingStateNodes = remaining.filter((n) => (n.data as WorkflowNodeData).nodeType === 'state');
                    const hasStartState = remaining.some((n) => {
                        const data = n.data as WorkflowNodeData;
                        return data.nodeType === 'state' && (data as StateNodeData).isStart;
                    });

                    // Only restore start state if we have other states but lost the start (should not happen with our guards)
                    if (!hasStartState && remainingStateNodes.length > 0 && currentWorkflow?.definition?.StartAt) {
                        const startStateName = currentWorkflow.definition.StartAt;
                        const defaultStartNode: Node<StateNodeData> = {
                            id: `state-${startStateName}`,
                            type: 'pass',
                            position: { x: 100, y: 200 },
                            data: {
                                nodeType: 'state',
                                label: startStateName,
                                stateType: 'Pass',
                                state: { Type: 'Pass', End: true },
                                isStart: true,
                                isEnd: true,
                                badges: [],
                            },
                        };
                        toast.info('Start state restored', {
                            description: 'The start state is required and has been automatically restored.',
                        });
                        return [...remaining, defaultStartNode];
                    }

                    return remaining;
                });
                // Also remove edges connected to deleted nodes
                setEdges((prev) => prev.filter((e) => !nodeIds.has(e.source) && !nodeIds.has(e.target)));
            }

            // Remove edges
            if (edgesToDelete.length > 0) {
                const edgeIds = new Set(edgesToDelete.map((e) => e.id));
                setEdges((prev) => prev.filter((e) => !edgeIds.has(e.id)));
            }
        },
        [setNodes, setEdges, currentWorkflow, nodes]
    );

    const {
        isOpen: isDeleteDialogOpen,
        isDeleting,
        targets: deleteTargets,
        requestDelete,
        confirmDelete,
        setIsOpen: setDeleteDialogOpen,
    } = useDeleteConfirmation<WorkflowNodeData, WorkflowEdgeData>({
        getNodeLabel: (node) => (node.data as WorkflowNodeData)?.label ?? node.id,
        getNodeType: (node) => {
            const data = node.data as WorkflowNodeData;
            return data.nodeType === 'state' ? (data as StateNodeData).stateType : data.nodeType;
        },
        getConnectedEdgeCount: (nodeId) =>
            edges.filter((e) => e.source === nodeId || e.target === nodeId).length,
        onDelete: handleDelete,
    });

    // ==========================================================================
    // Start State Protection & Keyboard Shortcuts
    // ==========================================================================

    /**
     * Filter out protected nodes: start marker (cannot be deleted) and start state unless it is the only state.
     * Returns nodes that can be deleted and shows a toast when protected nodes were in the selection.
     */
    const filterProtectedNodes = useCallback(
        (nodesToDelete: Node<WorkflowNodeData>[], allNodes: Node<WorkflowNodeData>[]): Node<WorkflowNodeData>[] => {
            const startMarkerInSelection = nodesToDelete.some((node) => node.id === '__start__' || (node.data as WorkflowNodeData).nodeType === 'startMarker');
            if (startMarkerInSelection) {
                toast.error('Cannot delete start marker', {
                    description: 'The start marker is required and cannot be deleted.',
                });
                nodesToDelete = nodesToDelete.filter((node) => node.id !== '__start__' && (node.data as WorkflowNodeData).nodeType !== 'startMarker');
            }

            const startNodesInSelection = nodesToDelete.filter((node) => {
                const data = node.data as WorkflowNodeData;
                return data.nodeType === 'state' && (data as StateNodeData).isStart;
            });

            if (startNodesInSelection.length === 0) {
                return nodesToDelete;
            }

            const stateNodesInGraph = allNodes.filter((n) => (n.data as WorkflowNodeData).nodeType === 'state');
            const isOnlyStateInWorkflow = stateNodesInGraph.length === 1;

            if (isOnlyStateInWorkflow) {
                return nodesToDelete;
            }

            toast.error('Cannot delete start state', {
                description: 'The start state is required and cannot be deleted. Add another step first, or delete all steps to clear the workflow.',
            });

            return nodesToDelete.filter((node) => {
                const data = node.data as WorkflowNodeData;
                return !(data.nodeType === 'state' && (data as StateNodeData).isStart);
            });
        },
        []
    );

    useFlowKeyboard({
        // Disable keyboard shortcuts when graph is locked (debug/replay mode)
        enabled: !isGraphLocked,
        onDeleteNodes: (selectedNodes) => {
            const deletableNodes = filterProtectedNodes(selectedNodes as Node<WorkflowNodeData>[], nodes as Node<WorkflowNodeData>[]);
            if (deletableNodes.length > 0) {
                requestDelete(deletableNodes, [], edges as Edge<WorkflowEdgeData>[]);
            }
        },
        onDeleteEdges: (selectedEdges) => {
            if (selectedEdges.length > 0) {
                requestDelete([], selectedEdges as Edge<WorkflowEdgeData>[], edges as Edge<WorkflowEdgeData>[]);
            }
        },
    });

    // ==========================================================================
    // Connection Validation
    // ==========================================================================

    const isValidConnection = useCallback(
        (connection: Connection): boolean => {
            // Validate using ASL rules
            const validation = validateConnection(connection, nodes as Node<WorkflowNodeData>[], edges);

            if (!validation.valid) {
                console.warn('Invalid connection:', validation.reason);
                return false;
            }

            // Check for cycles
            if (wouldCreateCycle(connection, edges)) {
                console.warn('This connection would create a cycle');
                toast.error('Connection rejected', {
                    description: 'This connection would create a cycle in the workflow.',
                });
                return false;
            }

            return true;
        },
        [nodes, edges]
    );

    const onConnect = useCallback(
        (connection: Connection) => {
            if (isGraphLocked) {
                return;
            }

            if (!isValidConnection(connection)) {
                return;
            }

            const sourceNode = nodes.find((n) => n.id === connection.source);
            const sourceData = sourceNode?.data as WorkflowNodeData | undefined;
            const isChoiceSource = sourceData?.nodeType === 'state' && 
                (sourceData as StateNodeData).stateType === 'Choice';
            const isTaskSource = sourceData?.nodeType === 'state' &&
                (sourceData as StateNodeData).stateType === 'Task';

            setEdges((eds) => {
                // Count existing outgoing transition edges from this source
                // (exclude catch, parallel, choice, default — only normal/dynamic count)
                const existingTransitionEdges = eds.filter((e) => {
                    if (e.source !== connection.source) return false;
                    const d = e.data as WorkflowEdgeData | undefined;
                    const t = d?.edgeType;
                    return !t || t === 'normal' || t === 'dynamic';
                });

                const willHaveMultipleOutputs = isTaskSource && existingTransitionEdges.length >= 1;

                let newEdgeData: WorkflowEdgeData;
                let markerColor: string;

                if (isChoiceSource) {
                    newEdgeData = { edgeType: 'choice' };
                    markerColor = '#1e40af';
                } else if (willHaveMultipleOutputs) {
                    const targetNode = nodes.find((n) => n.id === connection.target);
                    const targetLabel = targetNode ? (targetNode.data as WorkflowNodeData).label : '';
                    newEdgeData = { edgeType: 'dynamic', isDynamic: true, label: targetLabel };
                    markerColor = '#0891b2';
                } else {
                    newEdgeData = { edgeType: 'normal' };
                    markerColor = '#374151';
                }

                const newEdge: WorkflowEdgeType = {
                    id: `${connection.source}-${connection.target}`,
                    source: connection.source!,
                    sourceHandle: connection.sourceHandle ?? 'source',
                    target: connection.target!,
                    targetHandle: connection.targetHandle ?? 'target',
                    type: 'workflow',
                    data: newEdgeData,
                    markerEnd: { type: MarkerType.ArrowClosed, color: markerColor },
                };

                let updatedEdges = addEdge(newEdge, eds);

                // When a Task node gains multiple outputs, convert existing normal edges to dynamic
                if (willHaveMultipleOutputs) {
                    updatedEdges = updatedEdges.map((e) => {
                        if (e.source !== connection.source) return e;
                        const d = e.data as WorkflowEdgeData | undefined;
                        if (d?.edgeType === 'normal') {
                            const targetNode = nodes.find((n) => n.id === e.target);
                            const targetLabel = targetNode ? (targetNode.data as WorkflowNodeData).label : '';
                            return {
                                ...e,
                                data: { ...d, edgeType: 'dynamic' as EdgeType, isDynamic: true, label: targetLabel },
                                markerEnd: { type: MarkerType.ArrowClosed, color: '#0891b2' },
                            };
                        }
                        return e;
                    });
                }

                return updatedEdges;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setEdges, isValidConnection, nodes.length, edges.length, isGraphLocked]
    );

    // =========================================================================
    // Edge normalization: revert dynamic edges back to normal when a Task node
    // drops back to a single outgoing transition edge (e.g. after edge deletion)
    // =========================================================================
    const edgeNormalizationRef = useRef(false);
    useEffect(() => {
        if (edgeNormalizationRef.current) {
            edgeNormalizationRef.current = false;
            return;
        }

        // Build map: sourceId -> outgoing transition edges (normal | dynamic)
        const outgoingBySource = new Map<string, WorkflowEdgeType[]>();
        for (const edge of edges as WorkflowEdgeType[]) {
            const d = edge.data as WorkflowEdgeData | undefined;
            const t = d?.edgeType;
            if (t === 'catch' || t === 'parallel' || t === 'choice' || t === 'default') continue;
            const list = outgoingBySource.get(edge.source) || [];
            list.push(edge);
            outgoingBySource.set(edge.source, list);
        }

        let needsUpdate = false;
        const normalized = (edges as WorkflowEdgeType[]).map((edge) => {
            const d = edge.data as WorkflowEdgeData | undefined;
            if (d?.edgeType !== 'dynamic') return edge;

            const outgoing = outgoingBySource.get(edge.source) || [];
            if (outgoing.length <= 1) {
                needsUpdate = true;
                return {
                    ...edge,
                    data: { ...d, edgeType: 'normal' as EdgeType, isDynamic: false, label: undefined },
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#374151' },
                };
            }
            return edge;
        });

        if (needsUpdate) {
            edgeNormalizationRef.current = true;
            setEdges(normalized);
        }
    }, [edges, setEdges]);

    // ==========================================================================
    // Drag & Drop from Toolbox
    // ==========================================================================

    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();

            // Prevent dropping new nodes when graph is locked (debug/replay mode)
            if (isGraphLocked) {
                toast.info('Graph is locked', {
                    description: 'Exit debug or replay mode to edit the workflow.',
                });
                return;
            }

            const type = event.dataTransfer.getData('application/reactflow-type');
            const resourceData = event.dataTransfer.getData('application/reactflow-resource');
            const resourceName = event.dataTransfer.getData('application/reactflow-resource-name');
            const resourceType = event.dataTransfer.getData('application/reactflow-resource-type');

            if (!type) return;

            // Convert screen coordinates to flow coordinates (accounts for zoom/pan)
            // Do this early since we need it for both new agent/tool and regular drops
            const flowPosition = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            // Check if drop position is inside a container node (mapContainer)
            const containerNode = nodes.find((node) => {
                if (node.type !== 'mapContainer') return false;
                
                const nodeWidth = node.width ?? (node.style?.width as number) ?? 0;
                const nodeHeight = node.height ?? (node.style?.height as number) ?? 0;
                
                return (
                    flowPosition.x >= node.position.x &&
                    flowPosition.x <= node.position.x + nodeWidth &&
                    flowPosition.y >= node.position.y &&
                    flowPosition.y <= node.position.y + nodeHeight
                );
            });

            // Calculate position - relative to container if inside one, otherwise absolute
            let dropPosition = flowPosition;
            let dropParentId: string | undefined;
            
            if (containerNode) {
                dropPosition = {
                    x: flowPosition.x - containerNode.position.x,
                    y: flowPosition.y - containerNode.position.y,
                };
                dropParentId = containerNode.id;
            }

            // Handle new agent/tool creation - open form drawer and store drop position
            if (type === 'newAgent') {
                pendingNodeDropRef.current = { position: dropPosition, parentId: dropParentId };
                setIsAgentFormDrawerOpen(true);
                return;
            }
            if (type === 'newTool') {
                pendingNodeDropRef.current = { position: dropPosition, parentId: dropParentId };
                setIsToolFormDrawerOpen(true);
                return;
            }

            // For regular drops, use the already computed dropPosition and dropParentId
            const position = dropPosition;
            const parentId = dropParentId;

            // Handle fan-out/fan-in nodes specially (they use different data structure)
            if (type === 'fanOut' || type === 'fanIn') {
                const nodeData = {
                    nodeType: type === 'fanOut' ? 'parallelFork' : 'parallelJoin',
                    label: type === 'fanOut' ? 'Fork' : 'Join',
                    branchCount: 2,
                };

                const newNode: WorkflowNode = {
                    id: `${type}-${Date.now()}`,
                    type,
                    position,
                    data: nodeData as WorkflowNodeData,
                    ...(parentId && { 
                        parentId, 
                        extent: 'parent' as const,
                        expandParent: true,
                        zIndex: 1,
                    }),
                };

                // If parented, insert after parent to maintain ordering
                if (parentId) {
                    setNodes((nds) => {
                        const parentIndex = nds.findIndex((n) => n.id === parentId);
                        const newNodes = [...nds];
                        newNodes.splice(parentIndex + 1, 0, newNode);
                        return newNodes;
                    });
                } else {
                    setNodes((nds) => [...nds, newNode]);
                }
                
                toast.success(`Added ${type === 'fanOut' ? 'Fan-out' : 'Fan-in'} node`, {
                    description: type === 'fanOut' 
                        ? 'Parallel fork for concurrent execution.' 
                        : 'Parallel join for synchronization.',
                });
                return;
            }

            const stateType = (type.charAt(0).toUpperCase() + type.slice(1)) as StateNodeData['stateType'];
            
            // Use resource name if available, otherwise default label
            const label = resourceName || `New ${stateType}`;
            
            const nodeData: StateNodeData = {
                nodeType: 'state',
                label,
                stateType,
                state: {
                    Type: stateType,
                    ...(resourceData && { Resource: resourceData }),
                },
                // Store resource type for icon selection (agent, tool, model, workflow)
                ...(resourceType && { resourceType: resourceType as CapabilityType }),
                // Child nodes in a container should have handles visible
                isStart: false,
                isEnd: false,
            };

            const newNode: WorkflowNode = {
                id: `${type}-${Date.now()}`,
                type,
                position,
                data: nodeData,
                ...(parentId && { 
                    parentId, 
                    extent: 'parent' as const,
                    expandParent: true,
                    zIndex: 1,
                }),
            };

            // If parented, insert after parent to maintain React Flow's parent-before-child ordering
            if (parentId) {
                setNodes((nds) => {
                    const parentIndex = nds.findIndex((n) => n.id === parentId);
                    const newNodes = [...nds];
                    newNodes.splice(parentIndex + 1, 0, newNode);
                    return newNodes;
                });
                toast.success(`Added ${stateType} to Map iterator`, {
                    description: `${resourceType ? resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + ': ' : ''}${label}`,
                });
            } else {
                setNodes((nds) => [...nds, newNode]);
                const description = resourceType 
                    ? `${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}: ${label}`
                    : 'Double-click to configure settings.';
                toast.success(`Added ${stateType} state`, { description });
            }
        },
        [setNodes, screenToFlowPosition, nodes, isGraphLocked]
    );

    // ==========================================================================
    // Export Functionality
    // ==========================================================================

    const handleExport = useCallback(() => {
        const result = exportToAsl(nodes as WorkflowNode[], edges as WorkflowEdgeType[]);
        
        if (!result.valid) {
            // Open validation issues drawer instead of toast
            setValidationDrawer({
                isOpen: true,
                errors: result.errors,
                warnings: result.warnings,
            });
            return;
        }

        if (result.warnings.length > 0) {
            toast.warning('Exported with warnings', {
                description: `${result.warnings.length} warning(s) to review.`,
            });
        }

        // Format ASL JSON
        const aslJson = JSON.stringify(result.definition, null, 2);
        
        // Open export drawer
        setExportDrawer({
            isOpen: true,
            aslJson,
        });

        // Notify parent of definition change
        if (onDefinitionChange && result.definition) {
            onDefinitionChange(result.definition);
        }
    }, [nodes, edges, onDefinitionChange]);

    // Open validation drawer when clicking on issues indicator
    // Uses the same validationStatus that the button shows (from validateGraph)
    const handleShowIssues = useCallback(() => {
        const errors = validationStatus.issues.filter((i) => i.severity === 'error');
        const warnings = validationStatus.issues.filter((i) => i.severity === 'warning');
        setValidationDrawer({
            isOpen: true,
            errors,
            warnings,
        });
    }, [validationStatus.issues]);

    // Handle successful import - notify parent of definition change
    const handleImportSuccess = useCallback((definition: AslDefinition) => {
        if (onDefinitionChange) {
            onDefinitionChange(definition);
        }
        // The graph will re-render automatically when the parent updates the definition prop
        // which flows through WorkflowNavigationProvider and triggers useMemo recalculation
    }, [onDefinitionChange]);

    // ==========================================================================
    // Auto Layout
    // ==========================================================================

    const { fitView, zoomIn, zoomOut } = useReactFlow();

    const handleAutoLayout = useCallback((showToast = true) => {
        // Use the same layout calculation as initial render for consistency
        const finalNodes = calculateFullLayout(
            nodes as Node<WorkflowNodeData>[],
            edges as Edge<WorkflowEdgeData>[],
            { direction: layoutDirection, nodeSeparation: 50, rankSeparation: 150 }
        );

        setNodes(finalNodes);
        
        // After layout, notify React Flow that node handles have moved.
        // This is critical for edges to reconnect to the new handle positions.
        setTimeout(() => {
            const nodeIds = finalNodes.map((n) => n.id);
            nodeIds.forEach((id) => updateNodeInternals(id));
            fitView({ padding: 0.2, duration: 300 });
        }, 50);

        if (showToast) {
            const dirLabel = layoutDirection === 'LR' ? 'horizontal' : 'vertical';
            toast.success('Layout applied', {
                description: `${dirLabel.charAt(0).toUpperCase() + dirLabel.slice(1)} layout applied.`,
            });
        }
    }, [nodes, edges, setNodes, fitView, layoutDirection, updateNodeInternals]);

    // Keep ref in sync with the callback
    handleAutoLayoutRef.current = handleAutoLayout;

    // Fit view when workflow changes (layout is already calculated in useMemo)
    // This just ensures the view is centered on the graph
    useEffect(() => {
        if (pendingAutoLayout && pendingAutoLayout !== 'empty' && nodes.length > 0) {
            // Clear pending flag
            setPendingAutoLayout(null);
            
            // Wait for React Flow to fully initialize, then fit view
            const timer = setTimeout(() => {
                // Update node internals in case handle positions need recalculation
                nodes.forEach((n) => updateNodeInternals(n.id));
                fitView({ padding: 0.2, duration: 300 });
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [pendingAutoLayout, nodes, fitView, updateNodeInternals]);


    // ==========================================================================
    // Sub-workflow Navigation
    // ==========================================================================

    const handleNavigateToSubWorkflow = useCallback(
        (workflowKey: string) => {
            const subWorkflow = availableWorkflows.find((w) => w.key === workflowKey);
            if (subWorkflow) {
                pushWorkflow({
                    id: subWorkflow.id,
                    key: subWorkflow.key,
                    name: subWorkflow.name,
                    definition: subWorkflow.definition,
                });
            } else {
                console.warn(`Sub-workflow not found: ${workflowKey}`);
            }
        },
        [availableWorkflows, pushWorkflow]
    );

    // ==========================================================================
    // MiniMap Node Colors
    // ==========================================================================

    const getMiniMapNodeColor = useCallback((node: Node): string => {
        const data = node.data as WorkflowNodeData;
        
        if (data.nodeType === 'startMarker') return '#22c55e';
        if (data.nodeType === 'parallelFork' || data.nodeType === 'parallelJoin') return '#111827'; // Black
        if (data.nodeType === 'merge') return '#374151';
        
        if (data.nodeType === 'state') {
            const stateData = data as StateNodeData;
            switch (stateData.stateType) {
                case 'Task': {
                    // Color based on capability type - uses centralized colors
                    const resource = stateData.state?.Resource;
                    const resourceType = stateData.resourceType;
                    if (resourceType === 'agent' || resource?.startsWith('agent:')) return capabilityColors.agent.hex;
                    if (resourceType === 'tool' || resource?.startsWith('tool:')) return capabilityColors.tool.hex;
                    if (resourceType === 'model' || resource?.startsWith('model:')) return capabilityColors.model.hex;
                    if (resourceType === 'workflow' || resource?.startsWith('workflow:')) return capabilityColors.workflow.hex;
                    return defaultTaskColors.hex;
                }
                case 'Choice': return '#1e40af'; // Dark blue
                case 'Parallel': return '#111827'; // Black
                case 'Map': return '#10b981';
                case 'Succeed': return '#22c55e';
                case 'Fail': return '#ef4444';
                case 'Pass': return '#94a3b8';
                case 'Wait': return '#6366f1';
                default: return '#374151';
            }
        }
        
        return '#374151';
    }, []);

    // Build a map of node IDs to their parent map container state names
    // This helps us identify which nodes are inside a map iterator
    const nodeParentMapInfo = useMemo(() => {
        const info = new Map<string, string>();
        
        // First, identify all map container nodes and their state names
        const mapContainers = new Map<string, string>();
        for (const node of nodes) {
            if (node.type === 'mapContainer') {
                const nodeData = node.data as WorkflowNodeData;
                mapContainers.set(node.id, nodeData?.label || node.id);
            }
        }
        
        // Then, for each node with a parentId, check if parent is a map container
        for (const node of nodes) {
            if (node.parentId && mapContainers.has(node.parentId)) {
                info.set(node.id, mapContainers.get(node.parentId)!);
            }
        }
        
        return info;
    }, [nodes]);

    // Pre-compute per-node outgoing transition edge counts for dynamic routing detection
    const dynamicRouteInfo = useMemo(() => {
        const info = new Map<string, { isDynamic: boolean; targets: string[] }>();
        for (const node of nodes) {
            const nodeData = node.data as WorkflowNodeData;
            if (nodeData.nodeType !== 'state' || (nodeData as StateNodeData).stateType !== 'Task') continue;

            const outgoing = (edges as WorkflowEdgeType[]).filter((e) => {
                if (e.source !== node.id) return false;
                const d = e.data as WorkflowEdgeData | undefined;
                const t = d?.edgeType;
                return !t || t === 'normal' || t === 'dynamic';
            });

            if (outgoing.length >= 2) {
                const targets = outgoing
                    .map((e) => {
                        const targetNode = nodes.find((n) => n.id === e.target);
                        return targetNode ? (targetNode.data as WorkflowNodeData).label : '';
                    })
                    .filter(Boolean);
                info.set(node.id, { isDynamic: true, targets });
            }
        }
        return info;
    }, [nodes, edges]);

    // Callback for opening the dynamic routing drawer for a specific node
    const handleOpenDynamicNextDrawer = useCallback(
        (nodeId: string) => {
            const node = nodes.find((n) => n.id === nodeId);
            if (!node) return;
            const nodeData = node.data as StateNodeData;
            const routeInfo = dynamicRouteInfo.get(nodeId);
            const currentNext = nodeData.state?.Next ?? '';
            const isJsonPath = currentNext.startsWith('$.') || currentNext.startsWith('$$.');

            setDynamicNextDrawer({
                isOpen: true,
                nodeId,
                nodeName: nodeData.label,
                currentNextPath: isJsonPath ? currentNext : '',
                targets: routeInfo?.targets ?? [],
            });
        },
        [nodes, dynamicRouteInfo]
    );

    // Save dynamic Next path from the drawer
    const handleDynamicNextSave = useCallback(
        ({ nextPath }: { nextPath: string }) => {
            const { nodeId } = dynamicNextDrawer;
            if (!nodeId) return;

            setNodes((nds) =>
                nds.map((n) => {
                    if (n.id !== nodeId) return n;
                    const data = n.data as StateNodeData;
                    return {
                        ...n,
                        data: {
                            ...data,
                            state: { ...data.state, Next: nextPath },
                        },
                    };
                })
            );
        },
        [dynamicNextDrawer.nodeId, setNodes]
    );

    // Augment nodes with debug and replay props for highlighting and interaction
    const nodesWithDebugAndReplay = useMemo(() => {
        return nodes.map((node) => {
            const nodeData = node.data as WorkflowNodeData;
            const stateName = nodeData?.label || node.id.replace(/^state-/, '');
            
            // Check if this node is inside a map iterator
            const parentMapNodeId = nodeParentMapInfo.get(node.id);
            
            // Check if this node is inside a parallel branch (for checkpoint lookup)
            const parentParallelInfo = nodeData?.nodeType === 'state' 
                ? (nodeData as StateNodeData).parentParallelInfo 
                : undefined;
            
            // Get checkpoint data (will include aggregated data for map iteration nodes)
            // Also pass parallel branch info for nodes inside parallel branches
            const checkpointResult = replay.isReplayMode 
                ? replay.getCheckpointForNode(stateName, parentMapNodeId, parentParallelInfo) 
                : { isMapIteration: false, isParallelBranch: false, checkpoint: null, aggregated: null };

            // Check if this node has a child workflow run (for sub-workflow replay navigation)
            const childRun = replay.isReplayMode ? replay.childRunsByState.get(stateName) : undefined;
            const hasChildRunForReplay = replay.isReplayMode && replay.hasChildRun(stateName);
            
            // Determine replay status
            let replayStatus: StateNodeData['replayStatus'] = null;
            if (childRun) {
                replayStatus = childRun.status as StateNodeData['replayStatus'];
            } else if (checkpointResult.isMapIteration) {
                replayStatus = checkpointResult.aggregated?.aggregatedStatus || null;
            } else {
                replayStatus = checkpointResult.checkpoint?.status || null;
            }

            // Dynamic routing info (Task nodes only)
            const routeInfo = dynamicRouteInfo.get(node.id);
            const isDynamicRoute = routeInfo?.isDynamic ?? false;
            const isDynamicNextConfigured = isDynamicRoute && nodeData.nodeType === 'state'
                ? (() => {
                    const next = (nodeData as StateNodeData).state?.Next ?? '';
                    return next.startsWith('$.') || next.startsWith('$$.');
                })()
                : false;

            return {
                ...node,
                data: {
                    ...node.data,
                    // Debug props
                    isDebugMode: debug.isDebugMode,
                    isCurrentNode: debug.currentNode === node.id || debug.currentNode === nodeData?.label,
                    hasBreakpoint: debug.breakpoints.has(node.id) || debug.breakpoints.has(nodeData?.label || ''),
                    onToggleBreakpoint: (nodeId: string) => {
                        const label = (nodes.find(n => n.id === nodeId)?.data as WorkflowNodeData)?.label;
                        debug.toggleBreakpoint(label || nodeId);
                    },
                    // Replay props
                    isReplayMode: replay.isReplayMode,
                    replayCheckpoint: checkpointResult.checkpoint,
                    replayStatus,
                    replayAggregated: checkpointResult.aggregated,
                    isMapIterationNode: checkpointResult.isMapIteration,
                    parentMapNodeId,
                    onReplayNodeClick: handleNodeClickInReplay,
                    // Sub-workflow replay props
                    childRunId: childRun?.id,
                    hasChildRunForReplay,
                    // Tiled window callback for drill-down navigation
                    onOpenTiledWindow: effectiveOnOpenTiledWindow,
                    // Dynamic routing props (Task nodes only)
                    isDynamicRoute,
                    isDynamicNextConfigured,
                    onDynamicHandleClick: isDynamicRoute
                        ? () => handleOpenDynamicNextDrawer(node.id)
                        : undefined,
                },
            };
        });
    }, [nodes, nodeParentMapInfo, edges, dynamicRouteInfo, debug.isDebugMode, debug.currentNode, debug.breakpoints, debug.toggleBreakpoint, replay.isReplayMode, replay.getCheckpointForNode, replay.childRunsByState, replay.hasChildRun, handleNodeClickInReplay, effectiveOnOpenTiledWindow, handleOpenDynamicNextDrawer]);

    // Wrapper component - conditionally includes TiledWindowContainer
    // When embedded in a tiled window, we skip TiledWindowContainer to avoid infinite recursion
    const GraphWrapper = embedded ? 'div' : TiledWindowContainer;

    return (
        <div ref={graphContainerRef} className={cn('flex h-full w-full', graphSurface)}>
            {/* Main Graph Area - wrapped with TiledWindowContainer for drill-down windows (unless embedded) */}
            <GraphWrapper
                className={
                    embedded
                        ? cn('relative h-full w-full flex-1', graphSurface)
                        : undefined
                }
            >
                <ReactFlow
                    key={flowKey}
                    nodes={nodesWithDebugAndReplay}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    fitView
                    fitViewOptions={{ padding: 0.2 }}
                    minZoom={0.1}
                    elementsSelectable={true}
                    // Lock graph in debug/replay mode - prevent moving/connecting nodes
                    nodesDraggable={!isGraphLocked}
                    nodesConnectable={!isGraphLocked}
                    edgesReconnectable={!isGraphLocked}
                    // Disable ReactFlow's built-in delete handling - we use our own with confirmation
                    deleteKeyCode={null}
                    className={graphSurface}
                    defaultEdgeOptions={{
                        type: 'workflow',
                    }}
                >
                    <Background
                        variant={BackgroundVariant.Dots}
                        gap={16}
                        size={1}
                        color="#cbd5e1"
                        bgColor={transparentCanvas ? 'transparent' : undefined}
                    />
                    <MiniMap
                        nodeColor={getMiniMapNodeColor}
                        className="border-slate-200! bg-white!"
                        zoomable
                        pannable
                        nodeStrokeWidth={3}
                    />
                    {/* Custom Control Panel - top left, horizontal */}
                    <Panel position="top-left" className="flex items-center gap-2">
                        {/* Toolbar buttons */}
                        <div className="flex items-center rounded-md border border-slate-200 bg-white shadow-sm">
                            {/* Zoom In */}
                            <button
                                onClick={() => zoomIn()}
                                title="Zoom in"
                                className="flex h-7 w-7 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-50"
                            >
                                <Plus size={14} />
                            </button>
                            {/* Zoom Out */}
                            <button
                                onClick={() => zoomOut()}
                                title="Zoom out"
                                className="flex h-7 w-7 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-50"
                            >
                                <Minus size={14} />
                            </button>
                            {/* Fit View */}
                            <button
                                onClick={() => fitView({ padding: 0.2 })}
                                title="Fit view"
                                className="flex h-7 w-7 items-center justify-center border-r border-slate-200 text-slate-600 hover:bg-slate-50"
                            >
                                <Maximize2 size={14} />
                            </button>
                            {/* Horizontal layout */}
                            <button
                                onClick={() => {
                                    setLayoutDirection('LR');
                                    setTimeout(() => handleAutoLayoutRef.current?.(), 0);
                                }}
                                title="Apply horizontal layout"
                                className={cn(
                                    'flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50',
                                    layoutDirection === 'LR' ? 'bg-blue-50 text-blue-600' : 'text-slate-600'
                                )}
                            >
                                <ArrowRightFromLine size={14} />
                            </button>
                            {/* Vertical layout */}
                            <button
                                onClick={() => {
                                    setLayoutDirection('TB');
                                    setTimeout(() => handleAutoLayoutRef.current?.(), 0);
                                }}
                                title="Apply vertical layout"
                                className={cn(
                                    'flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50',
                                    layoutDirection === 'TB' ? 'bg-blue-50 text-blue-600' : 'text-slate-600'
                                )}
                            >
                                <ArrowDownFromLine size={14} />
                            </button>
                            {/* Debug - only enabled if workflow has tests */}
                            <button
                                onClick={debug.toggleDebugMode}
                                title={
                                    !hasTests 
                                        ? 'No test.json file available' 
                                        : debug.isDebugMode 
                                            ? 'Exit debug mode' 
                                            : 'Enter debug mode'
                                }
                                disabled={replay.isReplayMode || !hasTests}
                                className={cn(
                                    'flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50',
                                    debug.isDebugMode ? 'bg-amber-50 text-amber-600' : 'text-slate-600',
                                    (replay.isReplayMode || !hasTests) && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <Bug size={14} />
                            </button>
                            {/* Replay - only enabled if workflow has runs */}
                            <button
                                onClick={replay.isReplayMode ? handleExitReplayMode : handleEnterReplayMode}
                                title={
                                    !effectiveHasRuns 
                                        ? 'No workflow runs available' 
                                        : replay.isReplayMode 
                                            ? 'Exit replay mode' 
                                            : 'Enter replay mode'
                                }
                                disabled={debug.isDebugMode || !effectiveHasRuns}
                                className={cn(
                                    'flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50',
                                    replay.isReplayMode ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600',
                                    (debug.isDebugMode || !effectiveHasRuns) && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <History size={14} />
                            </button>
                            {/* Import */}
                            <button
                                onClick={() => setIsImportDrawerOpen(true)}
                                title="Import ASL definition"
                                disabled={isGraphLocked}
                                className={cn(
                                    'flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50 text-slate-600',
                                    isGraphLocked && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <Upload size={14} />
                            </button>
                            {/* Export */}
                            <button
                                onClick={validationStatus.canExport ? handleExport : handleShowIssues}
                                title={validationStatus.canExport ? 'Export ASL' : 'View validation issues'}
                                className={cn(
                                    'flex h-7 w-7 items-center justify-center border-r border-slate-200 hover:bg-slate-50',
                                    validationStatus.canExport ? 'text-green-600' : 'text-red-500'
                                )}
                            >
                                {validationStatus.canExport ? (
                                    <Download size={14} />
                                ) : (
                                    <AlertTriangle size={14} />
                                )}
                            </button>
                            {/* Settings */}
                            <button
                                onClick={() => setIsWorkflowSettingsOpen(true)}
                                title="Workflow Settings"
                                className="flex h-7 w-7 items-center justify-center text-slate-600 hover:bg-slate-50"
                            >
                                <Settings size={14} />
                            </button>
                        </div>

                        {/* Debug Toolbar - opens to the right of main toolbar when debug mode is active */}
                        {debug.isDebugMode && (
                            <WorkflowDebugToolbar
                                isDebugMode={debug.isDebugMode}
                                isDebugging={debug.isDebugging}
                                isPaused={debug.isPaused}
                                currentNode={debug.currentNode}
                                runId={debug.runId}
                                error={debug.error}
                                testCases={debug.testCases}
                                selectedTestCase={debug.selectedTestCase}
                                breakpointCount={debug.breakpoints.size}
                                onToggleDebugMode={debug.toggleDebugMode}
                                onSelectTestCase={debug.setSelectedTestCase}
                                onStart={debug.startDebug}
                                onStep={debug.step}
                                onContinue={debug.continueExecution}
                                onStop={debug.stopDebug}
                            />
                        )}

                        {/* Replay Toolbar - opens to the right of main toolbar when replay mode is active */}
                        {replay.isReplayMode && (
                            <WorkflowReplayToolbar
                                isReplayMode={replay.isReplayMode}
                                availableRuns={replay.availableRuns}
                                selectedRunId={replay.selectedRunId}
                                selectedRun={replay.selectedRun}
                                isLoading={replay.isLoading}
                                grafanaUrl={replay.grafanaUrl}
                                onExitReplay={handleExitReplayMode}
                                onSelectRun={replay.selectRun}
                                onRefresh={replay.refreshRuns}
                            />
                        )}

                    </Panel>

                </ReactFlow>

                {/* Hover zone for toolbox (right edge) - only when not pinned, closed, and no tiled windows open */}
                {!isToolboxPinned && !isToolboxOpen && !hasTiledWindowsOpen && (
                    <div
                        className="absolute right-0 top-0 h-full w-8 z-10 cursor-pointer bg-linear-to-l from-slate-200/60 to-transparent hover:from-slate-300/80 hover:w-10 transition-all duration-200"
                        onMouseEnter={handleToolboxMouseEnter}
                        onMouseLeave={() => setIsHoverSuppressed(false)}
                    >
                        {/* Visual hint - chevron icon positioned 1/3 from top */}
                        <div className="absolute right-0 top-1/3 flex items-center justify-center h-16 w-6 rounded-l-md bg-slate-300/70 hover:bg-slate-400/80 transition-colors">
                            <ChevronLeft size={16} className="text-slate-600" />
                        </div>
                    </div>
                )}
            </GraphWrapper>

            {/* Toolbox Sidebar Drawer - animated */}
            <div className="relative h-full">
                <WorkflowToolbox
                    isOpen={isToolboxOpen}
                    onClose={() => {
                        if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                        }
                        setIsToolboxOpen(false);
                        setIsToolboxPinned(false);
                    }}
                    agents={resolvedResources.agents}
                    tools={resolvedResources.tools}
                    models={resolvedResources.models}
                    policies={resolvedResources.policies}
                    workflows={availableWorkflows.map((w) => ({
                        name: w.name,
                        fqcn: (w as { fqcn?: string }).fqcn ?? w.key,
                        app_key: (w as { app_key?: string }).app_key,
                    }))}
                    isPinned={isToolboxPinned}
                    onPinToggle={() => setIsToolboxPinned(!isToolboxPinned)}
                    onMouseEnter={handleToolboxMouseEnter}
                    onMouseLeave={handleToolboxMouseLeave}
                />
            </div>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationDialog
                targets={deleteTargets}
                open={isDeleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={confirmDelete}
                isDeleting={isDeleting}
            />

            {/* Export ASL Drawer */}
            <ExportAslDrawer
                isOpen={exportDrawer.isOpen}
                onClose={() => setExportDrawer((prev) => ({ ...prev, isOpen: false }))}
                aslJson={exportDrawer.aslJson}
            />

            {/* Import ASL Drawer */}
            <ImportAslDrawer
                isOpen={isImportDrawerOpen}
                onClose={() => setIsImportDrawerOpen(false)}
                workflowId={currentWorkflow?.id || ''}
                workflowKey={currentWorkflow?.key || ''}
                appSlug={appSlug}
                onImportSuccess={handleImportSuccess}
            />

            {/* Node Settings Drawer - show appropriate drawer based on state type and resource type */}
            {(() => {
                // Use the explicit resourceType from node data (set during ASL conversion or drag-drop)
                // This is the authoritative source - do not infer from resource string patterns
                const resourceType = selectedNode?.resourceType;
                const stateType = selectedNode?.stateType;

                // Task Config Drawer - for Task nodes (comprehensive configuration)
                if (stateType === 'Task') {
                    // Build capabilities list from available resources
                    const capabilities: CapabilityMetadata[] = [
                        ...resolvedResources.agents.map((a) => ({
                            key: a.fqcn,
                            name: a.name,
                            version: '1.0.0',
                            description: a.description,
                            type: 'agent' as const,
                            executionMode: 'sync' as const,
                        })),
                        ...resolvedResources.tools.map((t) => ({
                            key: t.fqcn,
                            name: t.name,
                            version: '1.0.0',
                            description: t.description,
                            type: 'tool' as const,
                            executionMode: 'sync' as const,
                        })),
                        ...resolvedResources.models.map((m) => ({
                            key: m.fqcn,
                            name: m.name,
                            version: '1.0.0',
                            description: m.description,
                            type: 'model' as const,
                            executionMode: 'sync' as const,
                        })),
                        ...availableResources.workflows.map((w) => ({
                            key: (w as { fqcn?: string }).fqcn ?? w.name,
                            name: w.name,
                            version: '1.0.0',
                            type: 'workflow' as const,
                            executionMode: 'sync' as const,
                        })),
                    ];

                    // Get available node names for error routing
                    const availableNodeNames = nodes
                        .filter((n) => {
                            const data = n.data as WorkflowNodeData;
                            return data.nodeType === 'state' && data.label !== selectedNode?.label;
                        })
                        .map((n) => (n.data as WorkflowNodeData).label);

                    return (
                        <TaskConfigDrawer
                            isOpen={isSettingsOpen}
                            onClose={closeSettings}
                            nodeData={selectedNode}
                            readOnly={settingsReadOnly}
                            capabilities={capabilities}
                            availableNodes={availableNodeNames}
                            onSave={(nodeId, updates) => {
                                // Update the node in the graph
                                setNodes((nds) =>
                                    nds.map((n) => {
                                        const data = n.data as WorkflowNodeData;
                                        if (data.nodeType === 'state' && data.label === nodeId) {
                                            return {
                                                ...n,
                                                data: { ...data, ...updates },
                                            };
                                        }
                                        return n;
                                    })
                                );
                            }}
                        />
                    );
                }

                // Default node settings drawer for non-Task states (Pass, Choice, Wait, etc.)
                return (
                    <NodeSettingsDrawer
                        isOpen={isSettingsOpen}
                        onClose={closeSettings}
                        nodeData={selectedNode}
                        readOnly={settingsReadOnly}
                        availableResources={resolvedResources}
                    />
                );
            })()}

            {/* Workflow Settings Drawer */}
            <EditWorkflowDrawer
                isOpen={isWorkflowSettingsOpen}
                onClose={() => setIsWorkflowSettingsOpen(false)}
                workflowName={currentWorkflow?.name || ''}
                workflowKey={currentWorkflow?.key || ''}
                definition={currentWorkflow?.definition || { StartAt: '', States: {} }}
                metadata={workflowMetadata as any}
                readOnly={readOnly}
            />

            {/* Validation Issues Drawer */}
            <ValidationIssuesDrawer
                isOpen={validationDrawer.isOpen}
                onClose={() => setValidationDrawer((prev) => ({ ...prev, isOpen: false }))}
                errors={validationDrawer.errors}
                warnings={validationDrawer.warnings}
            />

            {/* Node Inspector Drawer - for replay mode */}
            <NodeInspectorDrawer
                isOpen={inspectorState.isOpen}
                onClose={() => setInspectorState((prev) => ({ ...prev, isOpen: false }))}
                stateName={inspectorState.stateName}
                capabilityName={inspectorState.capabilityName}
                checkpoint={inspectorState.checkpoint}
                logs={inspectorState.logs}
                aggregated={inspectorState.aggregated}
                isLoading={replay.isLoading}
            />

            {/* Add Agent drawer - same as agents list; for creating new agents via drag-drop */}
            {appSlug && (
                <CreateCapabilityDrawer
                    open={isAgentFormDrawerOpen}
                    onOpenChange={(open) => {
                        setIsAgentFormDrawerOpen(open);
                        if (!open) pendingNodeDropRef.current = null;
                    }}
                    appKey={appSlug}
                    capabilityType="agent"
                    onSuccess={handleAgentCreated}
                />
            )}

            {/* Tool Form Drawer - for creating new tools via drag-drop */}
            <ToolFormDrawer
                isOpen={isToolFormDrawerOpen}
                onClose={() => {
                    setIsToolFormDrawerOpen(false);
                    pendingNodeDropRef.current = null;
                }}
                appSlug={appSlug}
                onSuccess={handleToolCreated}
            />

            {/* Dynamic Next Drawer - for configuring JSONPath routing on Task nodes */}
            <DynamicNextDrawer
                isOpen={dynamicNextDrawer.isOpen}
                onClose={() => setDynamicNextDrawer((prev) => ({ ...prev, isOpen: false }))}
                nodeName={dynamicNextDrawer.nodeName}
                currentNextPath={dynamicNextDrawer.currentNextPath}
                targets={dynamicNextDrawer.targets}
                onSave={handleDynamicNextSave}
            />
        </div>
    );
}

// Provide WorkflowMode at the graph root (outside TiledWindowProvider) so nested
// tiled WorkflowGraph instances share replay/debug mode. Skip if a page already
// wrapped us in WorkflowModeProvider.
function EnsureWorkflowModeProvider({ children }: { children: ReactNode }) {
    const existing = useWorkflowModeOptional();
    if (existing) {
        return <>{children}</>;
    }
    return <WorkflowModeProvider>{children}</WorkflowModeProvider>;
}

// Main export wraps with FlowProvider, Navigation provider, and Settings provider
export function WorkflowGraph({
    definition,
    workflowId,
    workflowKey,
    workflowName,
    appSlug,
    readOnly = true,
    onDefinitionChange,
    onGraphChange,
    onNodeSettingsOpen,
    onNodeSettingsClose,
    availableWorkflows = [],
    agents = [],
    tools = [],
    models = [],
    policies = [],
    workflowMetadata,
    hasTests = false,
    hasRuns = false,
    onOpenTiledWindow,
    embedded = false,
    initialReplayRunId,
    graphSurfaceClassName,
    transparentCanvas = false,
}: WorkflowGraphProps) {
    const initialWorkflow: WorkflowStackItem = {
        id: workflowId,
        key: workflowKey,
        name: workflowName,
        definition,
        // Include replay run ID if provided (for opening in replay mode)
        replayRunId: initialReplayRunId,
    };

    return (
        <EnsureWorkflowModeProvider>
            <FlowProvider>
                <TiledWindowProvider>
                    <WorkflowNavigationProvider initialWorkflow={initialWorkflow}>
                        <NodeSettingsProvider
                            readOnly={readOnly}
                            agents={agents}
                            tools={tools}
                            models={models}
                            policies={policies}
                            workflows={availableWorkflows.map((w) => ({
                            name: w.name,
                            fqcn: (w as { fqcn?: string }).fqcn ?? w.key,
                            app_key: (w as { app_key?: string }).app_key,
                        }))}
                            onSettingsOpen={onNodeSettingsOpen}
                            onSettingsClose={onNodeSettingsClose}
                        >
                            <div className="flex h-full w-full flex-col">
                                {/* Breadcrumb navigation */}
                                <WorkflowBreadcrumb />

                                {/* Graph content */}
                                <div className="flex min-h-0 flex-1">
                                    <WorkflowGraphInner
                                        readOnly={readOnly}
                                        availableWorkflows={availableWorkflows}
                                        onDefinitionChange={onDefinitionChange}
                                        onGraphChange={onGraphChange}
                                        workflowMetadata={workflowMetadata}
                                        hasTests={hasTests}
                                        hasRuns={hasRuns}
                                        onOpenTiledWindow={onOpenTiledWindow}
                                        embedded={embedded}
                                        appSlug={appSlug}
                                        graphSurfaceClassName={graphSurfaceClassName}
                                        transparentCanvas={transparentCanvas}
                                    />
                                </div>
                            </div>
                        </NodeSettingsProvider>
                    </WorkflowNavigationProvider>
                </TiledWindowProvider>
            </FlowProvider>
        </EnsureWorkflowModeProvider>
    );
}
